import { useMixpanelContext } from "@portal/app/_components/context/MixpanelProvider";
import { useEffect, useRef } from "react";
import { readFormFunnelSessionId } from "../utils/formFunnelSessionStorage";
import { useFormFunnelTracking } from "./useFormFunnelTracking";

/** Adds funnel context to the shared provider's public-site analytics. */
export const useFormFunnelAnalytics = ({
  formFunnelId,
  funnelSlug,
  formFunnelSessionId,
  isAdmin,
  isPreview,
}: {
  formFunnelId: string;
  funnelSlug: string;
  formFunnelSessionId: string | undefined;
  isAdmin: boolean;
  isPreview: boolean;
}) => {
  const mixpanel = useMixpanelContext();
  const { trackEvent } = useFormFunnelTracking();
  const viewedFunnelRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (isAdmin || isPreview || !formFunnelSessionId) return;
    // A route change can render the next funnel before its session state updates.
    try {
      if (readFormFunnelSessionId(formFunnelId) !== formFunnelSessionId) return;
    } catch {
      return;
    }
    const properties = { formFunnelId, funnelSlug, formFunnelSessionId };
    const sdkReady = !!mixpanel.get_config("token");
    if (sdkReady) mixpanel.register(properties, { persistent: false });
    // This named event owns funnel-entry attribution, even if session hydration
    // follows the generic route view or the SDK is still loading.
    if (viewedFunnelRef.current !== formFunnelId) {
      viewedFunnelRef.current = formFunnelId;
      trackEvent("Form Funnel - Viewed", properties);
    }
    return () => {
      if (sdkReady)
        Object.keys(properties).forEach((key) => mixpanel.unregister(key, { persistent: false }));
    };
  }, [mixpanel, trackEvent, formFunnelId, funnelSlug, formFunnelSessionId, isAdmin, isPreview]);
};
